import { DataSet, Report, createColumnId, createColumnName, hasData } from '../../../types';
import { ColumnStats, calculateStats, calculateCorrelation } from '../../utils/StatisticsCalculator';
import { getColumnDataByName, getColumnIdByName } from '../../utils/column-utils';
import { HistogramWithReportActions } from '../HistogramWithReportActions';
import { ScatterPlotWithReportActions } from '../ScatterPlotWithReportActions';
import { MultiSeriesTimeSeriesPlotWithReportActions } from '../MultiSeriesTimeSeriesPlotWithReportActions';
import { RugPlot } from '../RugPlot';
import { StatisticsDisplay } from './StatisticsDisplay';
import { useState, useRef, useEffect, useMemo, useCallback, MouseEvent } from 'react';
import * as styles from '../../styles/components/statistics/column-statistics.css.ts';
import { ReportActionData } from '../ReportActions';

interface ColumnStatisticsProps {
  dataset: DataSet;
  selectedColumns: string[];
  filteredRowIndices?: number[] | null;
  selectedRows: number[];
  collapsedColumnStats: Record<string, boolean>;
  onToggleColumnStats: (columnName: string) => void;
  onHistogramBinClick?: (minValue: number, maxValue: number, dataIndices: number[], isAdditive?: boolean) => void;
  onRowSelect?: (rowIndices: number[]) => void;
  // レポート機能用
  availableReports?: Report[];
  onAddToReport?: (reportId: string, data: ReportActionData) => void;
  onCreateNewReport?: (data: ReportActionData) => void;
  // 図表サイズ用
  chartHeight?: number;
  scatterHeight?: number;
  // グループ化機能用
  groupByColumns?: string[];
}

/**
 * ColumnStatistics - 列統計表示コンポーネント
 * 
 * 選択された列の統計情報とヒストグラムを表示します：
 * - データ型別の統計情報
 * - 数値型列のヒストグラム
 * - ビン数調整機能
 */
function ColumnStatisticsInner({
  dataset,
  selectedColumns,
  filteredRowIndices,
  selectedRows,
  collapsedColumnStats,
  onToggleColumnStats,
  onHistogramBinClick,
  onRowSelect,
  availableReports,
  onAddToReport,
  onCreateNewReport,
  chartHeight,  // 外部から指定可能、未指定時は幅に基づいて計算
  scatterHeight = 200,
  groupByColumns = []
}: ColumnStatisticsProps) {
  // State to manage histogram bin count (independent for each column)
  const [histogramBins, setHistogramBins] = useState<Record<string, number>>({});
  // State to manage temporary bin input values (for handling partial input)
  const [binInputValues, setBinInputValues] = useState<Record<string, string>>({});
  // State to manage density estimation display (independent for each column)
  const [showDensity, setShowDensity] = useState<Record<string, boolean>>({});
  // State to manage collapsed state for each group
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // ケバブメニューの状態
  const [kebabMenuOpen, setKebabMenuOpen] = useState<string | null>(null);
  const [kebabMenuPosition, setKebabMenuPosition] = useState({ x: 0, y: 0 });
  const kebabMenuRef = useRef<HTMLDivElement>(null);

  // 親要素のサイズを動的に取得するためのref
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(340);
  const [shouldScroll, setShouldScroll] = useState<boolean>(false);

  // ヒストグラムの高さを幅に基づいて計算（アスペクト比 2:3）
  const calculatedChartHeight = useMemo(() => {
    // chartHeightが明示的に指定されていればそれを使用
    if (chartHeight !== undefined) return chartHeight;
    // そうでなければ幅の2/3を高さとする（最小220px、最大400px）
    return Math.min(400, Math.max(220, Math.floor(containerWidth * 2 / 3)));
  }, [containerWidth, chartHeight]);
  
  // 親要素の幅を監視してscatterWidthを動的に調整
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.offsetWidth;
        // paddingとマージンを考慮: .sidebarSection padding 20px * 2 + .scatterPlotWrapper padding 16px * 2 + border 1px * 2
        const usableWidth = parentWidth - (20 * 2) - (16 * 2) - (1 * 2);
        const preferredWidth = usableWidth;
        const minWidth = 250;
        
        // 最小幅を確保できない場合はスクロールを有効にする
        if (preferredWidth < minWidth) {
          setContainerWidth(minWidth);
          setShouldScroll(true);
        } else {
          setContainerWidth(preferredWidth);
          setShouldScroll(false);
        }
      }
    };

    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const getBinCount = useCallback((columnName: string): number => {
    return histogramBins[columnName] || 12;
  }, [histogramBins]);

  const handleBinCountChange = (columnName: string, newBinCount: number) => {
    setHistogramBins(prev => ({
      ...prev,
      [columnName]: newBinCount
    }));
  };

  const handleShowDensityChange = (columnName: string, show: boolean) => {
    setShowDensity(prev => ({
      ...prev,
      [columnName]: show
    }));
  };

  const getShowDensity = (columnName: string): boolean => {
    return showDensity[columnName] ?? true;
  };

  // ケバブメニューのハンドラー
  const handleKebabClick = useCallback((columnName: string, event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    let x = rect.right;
    let y = rect.bottom;

    // 画面外にはみ出さないように調整
    const menuWidth = 220;
    const menuHeight = 200;
    if (x + menuWidth > window.innerWidth) {
      x = rect.left - menuWidth;
    }
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 8;
    }

    setKebabMenuPosition({ x, y });
    setKebabMenuOpen(columnName);
  }, []);

  const handleAddToExistingReport = useCallback((reportId: string, columnName: string) => {
    if (onAddToReport) {
      const actionData: ReportActionData = {
        type: 'statistics_summary',
        title: `Statistics: ${columnName}`,
        statisticsSummaryData: {
          targetColumns: [columnName],
          datasetId: dataset.id
        }
      };
      onAddToReport(reportId, actionData);
    }
    setKebabMenuOpen(null);
  }, [onAddToReport, dataset.id]);

  const handleCreateNewReportWithStats = useCallback((columnName: string) => {
    if (onCreateNewReport) {
      const actionData: ReportActionData = {
        type: 'statistics_summary',
        title: `Statistics: ${columnName}`,
        statisticsSummaryData: {
          targetColumns: [columnName],
          datasetId: dataset.id
        }
      };
      onCreateNewReport(actionData);
    }
    setKebabMenuOpen(null);
  }, [onCreateNewReport, dataset.id]);

  // ヒストグラム用ケバブメニューのハンドラー
  const handleHistogramKebabClick = useCallback((columnName: string, event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    let x = rect.right;
    let y = rect.bottom;

    // 画面外にはみ出さないように調整
    const menuWidth = 220;
    const menuHeight = 200;
    if (x + menuWidth > window.innerWidth) {
      x = rect.left - menuWidth;
    }
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 8;
    }

    setKebabMenuPosition({ x, y });
    setKebabMenuOpen(`histogram_${columnName}`);
  }, []);

  const handleHistogramAddToExisting = useCallback((reportId: string, columnName: string) => {
    if (onAddToReport) {
      const columnId = getColumnIdByName(dataset, createColumnName(columnName)) || createColumnId(columnName);
      const actionData: ReportActionData = {
        type: 'histogram',
        title: `Histogram: ${columnName}`,
        histogramData: {
          columnName: columnId,
          dataset,
          bins: getBinCount(columnName)
        }
      };
      onAddToReport(reportId, actionData);
    }
    setKebabMenuOpen(null);
  }, [onAddToReport, dataset, getBinCount]);

  const handleHistogramCreateNew = useCallback((columnName: string) => {
    if (onCreateNewReport) {
      const columnId = getColumnIdByName(dataset, createColumnName(columnName)) || createColumnId(columnName);
      const actionData: ReportActionData = {
        type: 'histogram',
        title: `Histogram: ${columnName}`,
        histogramData: {
          columnName: columnId,
          dataset,
          bins: getBinCount(columnName)
        }
      };
      onCreateNewReport(actionData);
    }
    setKebabMenuOpen(null);
  }, [onCreateNewReport, dataset, getBinCount]);

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (kebabMenuRef.current && !kebabMenuRef.current.contains(event.target as Node)) {
        setKebabMenuOpen(null);
      }
    };

    if (kebabMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [kebabMenuOpen]);

  // カテゴリ件数クリック時のハンドラー
  const handleCategoryClick = useCallback((
    indices: number[],
    event: MouseEvent<HTMLSpanElement>
  ) => {
    if (!onRowSelect || indices.length === 0) return;

    const isAdditive = event.metaKey || event.ctrlKey;

    if (isAdditive) {
      // 追加選択モード
      const allSelected = indices.every(idx => selectedRows.includes(idx));
      if (allSelected) {
        // 全選択済み → 解除
        const newSelection = selectedRows.filter(idx => !indices.includes(idx));
        onRowSelect(newSelection);
      } else {
        // 未選択 → 追加
        const newSelection = [...new Set([...selectedRows, ...indices])];
        onRowSelect(newSelection);
      }
    } else {
      // 置換モード
      onRowSelect(indices);
    }
  }, [onRowSelect, selectedRows]);

  const renderHistogram = (columnName: string, stats: ColumnStats, groupIndices?: number[]) => {
    // datetime/date型の場合はRugPlotを表示
    if (stats.type === 'datetime' || stats.type === 'date') {
      const relevantIndices = groupIndices || filteredRowIndices || Array.from({ length: dataset.rowCount || 0 }, (_, i) => i);
      const columnData = getColumnDataByName(dataset, createColumnName(columnName));
      if (!columnData) return null;

      // datetime型のデータはISO文字列として保存されている
      // これをDateオブジェクトに変換する
      const dateData = relevantIndices
        .map(index => {
          const value = columnData[index];
          // ISO文字列をDateオブジェクトに変換
          if (value && typeof value === 'string') {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return { value: date, index };
            }
          }
          return null;
        })
        .filter((item): item is { value: Date; index: number } => item !== null);

      const dates = dateData.map(item => item.value);

      return (
        <>
          <div
            className={`${styles.histogramContainer} ${shouldScroll ? styles.scrollable : ''}`}
            style={{ width: '100%' }}
          >
            <h5 className={styles.histogramTitle}>
              Date Distribution
            </h5>
            <RugPlot
              data={dates}
              width={containerWidth}
              height={calculatedChartHeight}
              showDensity={true}
              originalData={dateData}
              selectedIndices={selectedRows}
              onTickClick={onRowSelect ? (dataIndices, isAdditive) => {
                if (isAdditive) {
                  const newSelection = [...new Set([...selectedRows, ...dataIndices])];
                  onRowSelect(newSelection);
                } else {
                  onRowSelect(dataIndices);
                }
              } : undefined}
            />
          </div>
        </>
      );
    }

    if (stats.type !== 'int64' && stats.type !== 'float64') {
      return null;
    }

    // ordinal尺度の場合はヒストグラムを表示しない
    if (stats.scale === 'ordinal') {
      return null;
    }

    return (
      <div
        className={`${styles.histogramContainer} ${shouldScroll ? styles.scrollable : ''}`}
        style={{ width: '100%' }}
      >
        <h5 className={styles.histogramTitle}>
          <span>Data Distribution</span>
          {availableReports && onAddToReport && onCreateNewReport && (
            <button
              className={styles.kebabMenuButton}
              onClick={(e) => handleHistogramKebabClick(columnName, e)}
              title="Add to Report"
            >
              <span className={styles.kebabMenuIcon}>⋮</span>
            </button>
          )}
        </h5>
        {/* ビン数選択コントロール */}
        <div className={styles.binCountControl}>
          <label className={styles.binCountLabel}>
            Bin count:
          </label>
          <div className={styles.binCountInputGroup}>
            <input 
              type="number" 
              min="2" 
              max="50" 
              value={binInputValues[columnName] ?? getBinCount(columnName)} 
              onChange={e => {
                const value = e.target.value;
                // 一時的な入力値を保存
                setBinInputValues(prev => ({
                  ...prev,
                  [columnName]: value
                }));
              }}
              onBlur={e => {
                const value = e.target.value;
                const numValue = Number(value);
                
                // 入力値をクリア
                setBinInputValues(prev => {
                  const newValues = { ...prev };
                  delete newValues[columnName];
                  return newValues;
                });
                
                // 有効な値の場合のみ更新
                if (!isNaN(numValue) && numValue >= 2 && numValue <= 50) {
                  handleBinCountChange(columnName, Math.floor(numValue));
                } else if (numValue < 2) {
                  handleBinCountChange(columnName, 2);
                } else if (numValue > 50) {
                  handleBinCountChange(columnName, 50);
                }
              }}
              className={styles.binCountInput}
            />
          </div>
          <label className={styles.densityCheckbox}>
            <input
              type="checkbox"
              checked={getShowDensity(columnName)}
              onChange={e => handleShowDensityChange(columnName, e.target.checked)}
            />
            Show density
          </label>
        </div>
        {(() => {
          // グループ化されている場合はgroupIndicesを、そうでなければfiltered indicesを使用
          const relevantIndices = groupIndices || filteredRowIndices || Array.from({ length: dataset.rowCount || 0 }, (_, i) => i);

          // 数値データを元のインデックスと共に取得
          const columnData = getColumnDataByName(dataset, createColumnName(columnName));
          if (!columnData) return null;

          const numericData = relevantIndices
            .map(index => ({ value: columnData[index] as number, index }))
            .filter(item => typeof item.value === 'number');

          const values = numericData.map(item => item.value);

            return (
              <HistogramWithReportActions
                data={values}
                width={containerWidth}
                height={calculatedChartHeight}
                bins={getBinCount(columnName)}
                title=""
                originalData={numericData}
                onBinClick={onRowSelect ? onHistogramBinClick : undefined}
                selectedIndices={selectedRows}
                // レポート機能用のプロパティ
                columnName={getColumnIdByName(dataset, createColumnName(columnName)) || createColumnId(columnName)}
                dataset={dataset}
                availableReports={availableReports || []}
                onAddToReport={onAddToReport!}
                onCreateNewReport={onCreateNewReport!}
                // 密度推定表示用のプロパティ
                showDensity={getShowDensity(columnName)}
              />
            );
        })()}
      </div>
    );
  };

  const handleScatterPlotRowClick = (dataIndices: number[], isAdditive: boolean = false) => {
    if (onRowSelect) {
      if (isAdditive) {
        // 追加選択モード：既存選択状態をチェックしてトグル選択・解除
        const currentlySelectedInPoints = dataIndices.filter(index => selectedRows.includes(index));

        if (currentlySelectedInPoints.length === dataIndices.length) {
          // すべてのポイントが既に選択されている場合は選択解除
          const remainingSelection = selectedRows.filter(index => !dataIndices.includes(index));
          onRowSelect(remainingSelection);
        } else {
          // 一部または全部が未選択の場合は追加選択
          const combinedSelection = [...new Set([...selectedRows, ...dataIndices])];
          onRowSelect(combinedSelection);
        }
      } else {
        // 通常選択モード：新しい選択で置き換え
        onRowSelect(dataIndices);
      }
    }
  };

  const renderColumnScatterPlots = (currentColumnName: string, groupKey: string, groupIndices?: number[]) => {
    // 現在の列が数値型でない場合は表示しない
    const currentColumn = dataset.schema.columns.find(col => col.name === currentColumnName);
    if (!currentColumn || (currentColumn.type !== 'int64' && currentColumn.type !== 'float64')) {
      return null;
    }

    // 他の選択された数値列を取得（現在の列を除く）
    const otherNumericColumns = selectedColumns.filter(columnName => {
      if (columnName === currentColumnName) return false;
      const column = dataset.schema.columns.find(col => col.name === columnName);
      return column && (column.type === 'int64' || column.type === 'float64');
    });

    // 他に数値列がない場合は表示しない
    if (otherNumericColumns.length === 0) {
      return null;
    }

    return (
      <div className={`${styles.columnScatterPlotsContainer} ${shouldScroll ? styles.scrollable : ''}`}>
        <h5 className={styles.scatterPlotSubsectionTitle}>
          Correlations with {currentColumnName}
        </h5>
        <div className={styles.scatterPlotsGrid}>
          {otherNumericColumns.map(otherColumn => {
            // 相関係数をキャッシュから取得
            const correlationCacheKey = `${groupKey}-${currentColumnName}-${otherColumn}`;
            const correlation = correlationCache.get(correlationCacheKey) ?? null;
            const correlationText = correlation !== null ? ` (r = ${correlation.toFixed(3)})` : '';

            return (
              <div key={`${currentColumnName}-${otherColumn}`} className={styles.scatterPlotItem}>
                <div className={styles.scatterPlotHeader}>
                  <span className={styles.scatterPlotTitle}>
                    vs {otherColumn}{correlationText}
                  </span>
                </div>
              <div className={`${styles.scatterPlotWrapper} ${shouldScroll ? styles.scrollable : ''}`}>
                <ScatterPlotWithReportActions
                  dataset={dataset}
                  xColumn={getColumnIdByName(dataset, createColumnName(currentColumnName)) || createColumnId(currentColumnName)}
                  yColumn={getColumnIdByName(dataset, createColumnName(otherColumn)) || createColumnId(otherColumn)}
                  filteredRowIndices={groupIndices || filteredRowIndices}
                  selectedRows={selectedRows}
                  width={containerWidth}
                  height={scatterHeight}
                  onPointClick={handleScatterPlotRowClick}
                  availableReports={availableReports || []}
                  onAddToReport={onAddToReport!}
                  onCreateNewReport={onCreateNewReport!}
                />
              </div>
            </div>
            );
          })}
        </div>
        <div className={styles.scatterPlotInfo}>
          <small>
            💡 Click points to select rows • Hold Ctrl/Cmd for multi-selection
          </small>
        </div>
      </div>
    );
  };

  const renderColumnTimeSeriesPlots = (currentColumnName: string, groupIndices?: number[]) => {
    const currentColumn = dataset.schema.columns.find(col => col.name === currentColumnName);
    
    if (!currentColumn) return null;

    // 日時型列の場合：すべての数値型列を1つの時系列プロットに統合して表示
    if (currentColumn.type === 'datetime' || currentColumn.type === 'date') {
      const numericColumns = selectedColumns.filter(columnName => {
        if (columnName === currentColumnName) return false;
        const column = dataset.schema.columns.find(col => col.name === columnName);
        return column && (column.type === 'int64' || column.type === 'float64');
      });

      if (numericColumns.length === 0) {
        return null;
      }

      return (
        <div className={`${styles.columnTimeSeriesContainer} ${shouldScroll ? styles.scrollable : ''}`}>
          <h5 className={styles.timeSeriesSubsectionTitle}>
            Time Series with {currentColumnName}
          </h5>
          <div className={styles.timeSeriesGrid}>
            <div className={styles.timeSeriesItem}>
              <div className={styles.timeSeriesHeader}>
                <span className={styles.timeSeriesTitle}>
                  Multiple series over time
                </span>
              </div>
              <div className={`${styles.timeSeriesWrapper} ${shouldScroll ? styles.scrollable : ''}`}>
                <MultiSeriesTimeSeriesPlotWithReportActions
                  dataset={dataset}
                  dateColumn={getColumnIdByName(dataset, createColumnName(currentColumnName)) || createColumnId(currentColumnName)}
                  valueColumns={numericColumns.map(col => getColumnIdByName(dataset, createColumnName(col)) || createColumnId(col))}
                  filteredRowIndices={groupIndices || filteredRowIndices}
                  selectedRows={selectedRows}
                  width={containerWidth}
                  height={scatterHeight + 40} // 凡例のスペースを追加
                  onPointClick={handleTimeSeriesRowClick}
                  availableReports={availableReports || []}
                  onAddToReport={onAddToReport!}
                  onCreateNewReport={onCreateNewReport!}
                />
              </div>
            </div>
          </div>
          <div className={styles.timeSeriesInfo}>
            <small>
              💡 Click points to select rows • Hold Ctrl/Cmd for multi-selection • Double-click to reset zoom
            </small>
          </div>
        </div>
      );
    }

    // 数値型列の場合：すべての日時型列との時系列プロットを表示
    if (currentColumn.type === 'int64' || currentColumn.type === 'float64') {
      const dateColumns = selectedColumns.filter(columnName => {
        if (columnName === currentColumnName) return false;
        const column = dataset.schema.columns.find(col => col.name === columnName);
        return column && (column.type === 'datetime' || column.type === 'date');
      });

      if (dateColumns.length === 0) {
        return null;
      }

      return (
        <div className={`${styles.columnTimeSeriesContainer} ${shouldScroll ? styles.scrollable : ''}`}>
          <h5 className={styles.timeSeriesSubsectionTitle}>
            {currentColumnName} over Time
          </h5>
          <div className={styles.timeSeriesGrid}>
            {dateColumns.map(dateColumn => (
              <div key={`${dateColumn}-${currentColumnName}`} className={styles.timeSeriesItem}>
                <div className={styles.timeSeriesHeader}>
                  <span className={styles.timeSeriesTitle}>
                    vs {dateColumn}
                  </span>
                </div>
                <div className={`${styles.timeSeriesWrapper} ${shouldScroll ? styles.scrollable : ''}`}>
                  <MultiSeriesTimeSeriesPlotWithReportActions
                    dataset={dataset}
                    dateColumn={getColumnIdByName(dataset, createColumnName(dateColumn)) || createColumnId(dateColumn)}
                    valueColumns={[getColumnIdByName(dataset, createColumnName(currentColumnName)) || createColumnId(currentColumnName)]}
                    filteredRowIndices={groupIndices || filteredRowIndices}
                    selectedRows={selectedRows}
                    width={containerWidth}
                    height={scatterHeight}
                    onPointClick={handleTimeSeriesRowClick}
                    availableReports={availableReports || []}
                    onAddToReport={onAddToReport!}
                    onCreateNewReport={onCreateNewReport!}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className={styles.timeSeriesInfo}>
            <small>
              💡 Click points to select rows • Hold Ctrl/Cmd for multi-selection • Double-click to reset zoom
            </small>
          </div>
        </div>
      );
    }

    return null;
  };

  const handleTimeSeriesRowClick = (dataIndices: number[], isAdditive: boolean = false) => {
    if (onRowSelect) {
      if (isAdditive) {
        // 追加選択モード：既存選択状態をチェックしてトグル選択・解除
        const currentlySelectedInPoints = dataIndices.filter(index => selectedRows.includes(index));

        if (currentlySelectedInPoints.length === dataIndices.length) {
          // すべてのポイントが既に選択されている場合は選択解除
          const remainingSelection = selectedRows.filter(index => !dataIndices.includes(index));
          onRowSelect(remainingSelection);
        } else {
          // 一部または全部が未選択の場合は追加選択
          const combinedSelection = [...new Set([...selectedRows, ...dataIndices])];
          onRowSelect(combinedSelection);
        }
      } else {
        // 通常選択モード：新しい選択で置き換え
        onRowSelect(dataIndices);
      }
    }
  };

  // グループ化されたデータをuseMemoでキャッシュ
  const groupedData = useMemo(() => {
    if (!dataset || groupByColumns.length === 0) {
      // グループ化しない場合は全データを1つのグループとして返す
      return [{ groupKey: 'All Data', indices: filteredRowIndices || Array.from({ length: dataset?.rowCount || 0 }, (_, i) => i) }];
    }

    const relevantIndices = filteredRowIndices || Array.from({ length: dataset.rowCount || 0 }, (_, i) => i);
    const groups = new Map<string, number[]>();

    relevantIndices.forEach(rowIndex => {
      const groupValues = groupByColumns.map(colName => {
        const columnData = getColumnDataByName(dataset, createColumnName(colName));
        return columnData ? String(columnData[rowIndex] || 'null') : 'null';
      });
      const groupKey = groupValues.join(' | ');

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(rowIndex);
    });

    return Array.from(groups.entries()).map(([groupKey, indices]) => ({
      groupKey,
      indices
    }));
  }, [dataset, groupByColumns, filteredRowIndices]);

  // 統計情報をキャッシュ（全グループ・全列の統計を一度に計算）
  const statsCache = useMemo(() => {
    if (!hasData(dataset)) return new Map<string, ColumnStats>();

    const cache = new Map<string, ColumnStats>();
    groupedData.forEach(group => {
      selectedColumns.forEach(columnName => {
        const cacheKey = `${group.groupKey}-${columnName}`;
        const stats = calculateStats(dataset, createColumnName(columnName), group.indices);
        if (stats) {
          cache.set(cacheKey, stats);
        }
      });
    });

    return cache;
  }, [dataset, groupedData, selectedColumns]);

  // 相関係数をキャッシュ（全列ペア・全グループの相関を一度に計算）
  const correlationCache = useMemo(() => {
    if (!hasData(dataset)) return new Map<string, number | null>();

    const cache = new Map<string, number | null>();
    groupedData.forEach(group => {
      selectedColumns.forEach(currentColumn => {
        const currentCol = dataset.schema.columns.find(col => col.name === currentColumn);
        if (!currentCol || (currentCol.type !== 'int64' && currentCol.type !== 'float64')) return;

        selectedColumns.forEach(otherColumn => {
          if (currentColumn === otherColumn) return;
          const otherCol = dataset.schema.columns.find(col => col.name === otherColumn);
          if (!otherCol || (otherCol.type !== 'int64' && otherCol.type !== 'float64')) return;

          const cacheKey = `${group.groupKey}-${currentColumn}-${otherColumn}`;
          const correlation = calculateCorrelation(
            dataset,
            createColumnName(currentColumn),
            createColumnName(otherColumn),
            group.indices
          );
          cache.set(cacheKey, correlation);
        });
      });
    });

    return cache;
  }, [dataset, groupedData, selectedColumns]);

  if (selectedColumns.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className={styles.sidebarSection}>
      {groupedData.map(group => (
        <div key={group.groupKey} className={styles.groupSection} data-section-group={groupByColumns.length > 0 ? group.groupKey : ''}>
          {groupByColumns.length > 0 && (
            <h3 
              className={`${styles.groupTitle} ${styles.collapsibleGroupHeader} ${collapsedGroups[group.groupKey] ? styles.collapsed : ''}`}
              onClick={() => setCollapsedGroups(prev => ({
                ...prev,
                [group.groupKey]: !prev[group.groupKey]
              }))}
            >
              <span className={styles.groupTitleText}>
                {group.groupKey} ({group.indices.length} rows)
              </span>
              <span className={styles.groupToggleIcon}>
                {collapsedGroups[group.groupKey] ? '▶' : '▼'}
              </span>
            </h3>
          )}
          {!collapsedGroups[group.groupKey] && selectedColumns.map(columnName => {
            const cacheKey = `${group.groupKey}-${columnName}`;
            const stats = statsCache.get(cacheKey);
            if (!stats) return null;

            const uniqueKey = `${group.groupKey}-${columnName}`;

            return (
              <div key={uniqueKey} className={styles.columnStats} data-section-column={columnName}>
                <div className={styles.columnStatsHeader}>
                  <h4
                    className={`${styles.columnStatsTitle} ${styles.collapsibleHeader} ${styles.columnStatsClickable}`}
                    onClick={() => onToggleColumnStats(uniqueKey)}
                  >
                    {columnName} {collapsedColumnStats[uniqueKey] ? '▼' : '▲'}
                  </h4>
                  {availableReports && onAddToReport && onCreateNewReport && (
                    <button
                      className={styles.kebabMenuButton}
                      title="Add to Report"
                      onClick={(e) => handleKebabClick(columnName, e)}
                    >
                      <span className={styles.kebabMenuIcon}>⋮</span>
                    </button>
                  )}
                </div>
                {!collapsedColumnStats[uniqueKey] && (
                  <>
                    <StatisticsDisplay
                      stats={stats}
                      onCategoryClick={onRowSelect ? handleCategoryClick : undefined}
                    />
                    {renderHistogram(columnName, stats, group.indices)}
                    {renderColumnScatterPlots(columnName, group.groupKey, group.indices)}
                    {renderColumnTimeSeriesPlots(columnName, group.indices)}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* ケバブメニューのドロップダウン */}
      {kebabMenuOpen && (
        <div
          ref={kebabMenuRef}
          className={styles.kebabDropdown}
          style={{
            position: 'fixed',
            left: kebabMenuPosition.x,
            top: kebabMenuPosition.y,
          }}
        >
          <div className={styles.kebabDropdownHeader}>
            Add to Report
          </div>
          {kebabMenuOpen.startsWith('histogram_') ? (
            // ヒストグラム用メニュー
            <>
              <button
                className={styles.kebabDropdownItem}
                onClick={() => handleHistogramCreateNew(kebabMenuOpen.replace('histogram_', ''))}
              >
                + Create New Report
              </button>
              {availableReports && availableReports.length > 0 && (
                <>
                  <div className={styles.kebabDropdownDivider} />
                  <div className={styles.kebabDropdownLabel}>Existing Reports</div>
                  {availableReports.map(report => (
                    <button
                      key={report.id}
                      className={styles.kebabDropdownItem}
                      onClick={() => handleHistogramAddToExisting(report.id, kebabMenuOpen.replace('histogram_', ''))}
                    >
                      {report.name}
                    </button>
                  ))}
                </>
              )}
            </>
          ) : (
            // 統計用メニュー
            <>
              <button
                className={styles.kebabDropdownItem}
                onClick={() => handleCreateNewReportWithStats(kebabMenuOpen)}
              >
                + Create New Report
              </button>
              {availableReports && availableReports.length > 0 && (
                <>
                  <div className={styles.kebabDropdownDivider} />
                  <div className={styles.kebabDropdownLabel}>Existing Reports</div>
                  {availableReports.map(report => (
                    <button
                      key={report.id}
                      className={styles.kebabDropdownItem}
                      onClick={() => handleAddToExistingReport(report.id, kebabMenuOpen)}
                    >
                      {report.name}
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ColumnStatistics(props: ColumnStatisticsProps) {
  return <ColumnStatisticsInner {...props} />;
}
